import React, { useEffect, useState } from "react";
import { Brandmark, Crest } from "@/components/Brand";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Plus, X, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const C = { email: "info@solvanagroup.com", site: "solvanagroup.com", uk: "+44 7512 328849", es: "+34 636 400 331" };
const ink = "#1a1712";
const gold = "#C79A4B";

function GoldRule({ w = 54 }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, width: w }}>
      <span style={{ height: 1, flex: 1, background: gold }} />
      <span style={{ width: 5, height: 5, background: gold, transform: "rotate(45deg)", flexShrink: 0 }} />
      <span style={{ height: 1, flex: 1, background: gold }} />
    </span>
  );
}

export default function RateCard() {
  const nav = useNavigate();
  const [rc, setRc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/rate-card").then((r) => setRc(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!rc) return <div style={{ padding: 40 }}>Loading…</div>;

  const setPlan = (i, k, v) => setRc({ ...rc, plans: rc.plans.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const setSvc = (i, k, v) => setRc({ ...rc, services: rc.services.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const addSvc = () => setRc({ ...rc, services: [...rc.services, { service: "", unit: "per visit", price: "" }] });
  const rmSvc = (i) => setRc({ ...rc, services: rc.services.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try { await api.put("/rate-card", rc); toast.success("Rate card saved."); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const doPrint = () => {
    let sty = document.getElementById("print-page-style");
    if (!sty) { sty = document.createElement("style"); sty.id = "print-page-style"; document.head.appendChild(sty); }
    sty.textContent = "@page { size: 210mm 297mm; margin: 0; }";
    document.querySelectorAll(".crop-wrap").forEach((el) => el.classList.remove("print-target"));
    document.querySelector('[data-print-id="ratecard-sheet"]').classList.add("print-target");
    document.body.classList.add("printing");
    window.print();
    setTimeout(() => document.body.classList.remove("printing"), 400);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      <div className="no-print" style={{ maxWidth: 1000, margin: "0 auto", padding: "32px clamp(20px,5vw,40px) 80px" }}>
        <button className="link-u" onClick={() => nav("/staff")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", marginBottom: 20 }}><ArrowLeft size={14} /> Back to office</button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h1 style={{ fontSize: "clamp(30px,4vw,46px)", margin: "0 0 8px" }}>Rate card</h1>
            <p style={{ color: "var(--muted)", maxWidth: "56ch" }}>Staff reference for what we charge. Edit any figure and <b>Save</b>, then <b>Print client sheet</b> for a branded A4 to hand out.</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-outline" onClick={save} disabled={saving} data-testid="ratecard-save" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Save size={15} /> {saving ? "Saving…" : "Save"}</button>
            <button className="btn-ink" onClick={doPrint} data-testid="ratecard-print" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Printer size={15} /> Print client sheet</button>
          </div>
        </div>

        {/* EDITABLE STAFF TABLE */}
        <h2 style={{ fontSize: 22, margin: "34px 0 14px" }}>Care plans (monthly)</h2>
        <div className="card-soft" style={{ padding: 18, display: "grid", gap: 12 }}>
          {rc.plans.map((p, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.6fr 1.2fr 1.6fr", gap: 10 }} data-testid={`plan-row-${i}`}>
              <input className="inp" value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} placeholder="Plan" />
              <input className="inp" value={p.visits} onChange={(e) => setPlan(i, "visits", e.target.value)} placeholder="Visits" />
              <input className="inp" value={p.price} onChange={(e) => setPlan(i, "price", e.target.value)} placeholder="€/mo" />
              <input className="inp" value={p.note} onChange={(e) => setPlan(i, "note", e.target.value)} placeholder="Positioning" />
            </div>
          ))}
        </div>

        <h2 style={{ fontSize: 22, margin: "30px 0 14px" }}>À la carte services</h2>
        <div className="card-soft" style={{ padding: 18, display: "grid", gap: 12 }}>
          {rc.services.map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr auto", gap: 10, alignItems: "center" }} data-testid={`service-row-${i}`}>
              <input className="inp" value={s.service} onChange={(e) => setSvc(i, "service", e.target.value)} placeholder="Service" />
              <input className="inp" value={s.unit} onChange={(e) => setSvc(i, "unit", e.target.value)} placeholder="Unit" />
              <input className="inp" value={s.price} onChange={(e) => setSvc(i, "price", e.target.value)} placeholder="Price" />
              <button type="button" className="cal-nav" onClick={() => rmSvc(i)} aria-label="Remove" data-testid={`service-remove-${i}`}><X size={16} /></button>
            </div>
          ))}
          <button type="button" className="link-u" onClick={addSvc} data-testid="service-add" style={{ display: "inline-flex", alignItems: "center", gap: 6, justifySelf: "start" }}><Plus size={14} /> Add service</button>
        </div>

        <div style={{ marginTop: 20 }}>
          <span className="lbl-m" style={{ display: "block", marginBottom: 4 }}>Footnote</span>
          <textarea className="inp" rows={2} value={rc.footnote} onChange={(e) => setRc({ ...rc, footnote: e.target.value })} data-testid="ratecard-footnote" />
        </div>

        <div style={{ borderTop: "1px solid var(--line)", margin: "34px 0 20px" }} />
        <h2 style={{ fontSize: 22, margin: "0 0 14px" }}>Client sheet preview</h2>
      </div>

      {/* PRINTABLE A4 CLIENT SHEET */}
      <div className="print-stage">
        <div className="crop-wrap" data-print-id="ratecard-sheet">
          <div className="print-artifact" style={{ width: "210mm", minHeight: "297mm", background: "var(--paper)", padding: "18mm 20mm", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "0.4mm solid #e2d8c7", paddingBottom: "7mm" }}>
              <Brandmark size={40} fontSize={22} />
              <div style={{ textAlign: "right", fontSize: 8.5, color: "#5b544a", lineHeight: 1.8 }}>
                <div style={{ color: gold }}>{C.site}</div><div>{C.email}</div><div>{C.uk} · {C.es}</div>
              </div>
            </div>

            <div style={{ textAlign: "center", margin: "10mm 0 8mm" }}>
              <div style={{ fontSize: 8.5, letterSpacing: ".26em", textTransform: "uppercase", color: gold }}>Property care · Mallorca</div>
              <h1 style={{ fontFamily: "var(--serif)", fontSize: 30, fontWeight: 400, color: ink, margin: "3mm 0 0" }}>Services & rates</h1>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "3mm" }}><GoldRule w={60} /></div>
            </div>

            <div style={{ fontSize: 9, letterSpacing: ".22em", textTransform: "uppercase", color: gold, marginBottom: "3mm" }}>Care plans</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "5mm", marginBottom: "9mm" }}>
              {rc.plans.map((p, i) => (
                <div key={i} style={{ border: "0.4mm solid #e2d8c7", padding: "5mm", textAlign: "center" }}>
                  <div style={{ fontFamily: "var(--serif)", fontSize: 15, color: ink }}>{p.name}</div>
                  <div style={{ fontSize: 8, color: "#8b8478", marginTop: "1mm" }}>{p.visits}</div>
                  <div style={{ fontFamily: "var(--serif)", fontSize: 20, color: ink, margin: "3mm 0 1mm" }}>{p.price}</div>
                  <div style={{ fontSize: 7.5, letterSpacing: ".08em", color: gold }}>per month</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 9, letterSpacing: ".22em", textTransform: "uppercase", color: gold, marginBottom: "3mm" }}>À la carte services</div>
            <div style={{ display: "grid", gap: 0 }}>
              {rc.services.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "2.6mm 0", borderBottom: "0.3mm solid #e6ddce" }}>
                  <span style={{ fontSize: 11, color: ink }}>{s.service}</span>
                  <span style={{ display: "flex", alignItems: "baseline", gap: "4mm" }}>
                    <span style={{ fontSize: 8, color: "#8b8478" }}>{s.unit}</span>
                    <span style={{ fontSize: 12, fontFamily: "var(--serif)", color: ink, minWidth: "24mm", textAlign: "right" }}>{s.price}</span>
                  </span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "auto", paddingTop: "8mm" }}>
              <p style={{ fontSize: 8, color: "#8b8478", lineHeight: 1.6, borderTop: "0.4mm solid #e2d8c7", paddingTop: "5mm" }}>{rc.footnote}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
